// The workspace chrome: top nav, brand mark, theme toggle, user chip, dev-stub warning.
// Ported from the Nunjucks layout it replaces, so the light look is unchanged.
//
// Every colour here comes from a semantic token (bg-surface, text-ink, border-line —
// see the note at the top of src/styles/app.css), so the chrome flips theme without
// a single `dark:` variant. The two exceptions are deliberate and commented.

import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';

import { useSession, useCanWrite, useSignOut } from '../session/SessionProvider.jsx';
import { ThemeProvider } from '../theme/ThemeProvider.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';
import { visibleSections } from './nav-items.js';

// Routes that render a DATA TABLE rather than prose, and so should use the whole
// monitor instead of the reading width.
//
// docs/ADMIN-CONSOLE-PLAN.md §2 fix #2 is explicit about why this is a per-route
// decision and not a global one: on a 1920px monitor roughly 704px of every screen
// was empty margin while the queue's ten columns truncated mid-word — but the same
// max-w-7xl is the RIGHT width for ticket detail, where a message body spanning
// 1900px is worse to read, not better. So the default stays the reading width and
// exactly the routes below opt out. `/tickets/new` and `/tickets/:publicRef` are
// deliberately absent.
//
// Matched here rather than passed up from the page, because <Outlet /> renders the
// page BELOW this element: a page cannot widen a container it is already inside
// without lifting state into the layout and re-rendering the whole shell on every
// navigation. Both class names are written out as complete literals so Tailwind's
// source scanner still sees them.
const WIDE_CONTENT_ROUTES = ['/tickets'];

function initials(name) {
  return String(name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

export default function AppLayout() {
  const user = useSession();
  const canWrite = useCanWrite();
  // Sections, not a second hardcoded list: web/src/layout/nav-items.js owns the
  // one list App.jsx's routes are checked against at boot. Filtering here is
  // presentation — the API refuses what it hides.
  const sections = visibleSections(user);
  const { pathname } = useLocation();
  // 120rem = 1920px, the widest common desktop panel — so the queue fills a 1920
  // monitor edge to edge (minus the same lg:px-8 buffer the header uses) and still
  // stops growing on an ultrawide, where a 3440px-wide table would be unreadable.
  const contentWidth = WIDE_CONTENT_ROUTES.includes(pathname) ? 'max-w-[120rem]' : 'max-w-7xl';
  const signOut = useSignOut();
  const [signOutError, setSignOutError] = useState(null);
  const [signingOut, setSigningOut] = useState(false);

  // A sign-out that FAILED must not look like one that worked — the whole point of
  // the control is a shared desk, and "I clicked it, so I'm out" is the assumption it
  // has to earn. A stale CSRF token is the realistic failure (403), and it is
  // recoverable: reload, GET /api/me mints a fresh one, try again.
  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await signOut();
    } catch (error) {
      setSigningOut(false);
      setSignOutError(error?.message ?? 'Sign-out failed. Reload the page and try again.');
    }
  }

  return (
    // ThemeProvider sits here rather than in App because the toggle is part of the
    // chrome and nothing above the layout reads theme state — <html> already carries
    // the right class by the time React mounts (web/index.html boot script).
    <ThemeProvider>
      <div className="min-h-full">
        <nav className="bg-surface border-b border-line">
          {/* CHROME, not content: no max-width, so the nav sits against the left
              edge and the action cluster against the right, with only the layout's
              own horizontal padding scale (px-4 / sm:px-6 / lg:px-8) as the buffer.
              This is global on purpose — the header is the same bar on every route,
              and it is the one element that should always span the viewport. It is
              NOT the same decision as the content width below, which stays the
              reading width everywhere except the queue.

              The responsive classes inside are what keep this working narrow: the
              nav links are `hidden sm:flex` and the name block `hidden sm:block`,
              so below 640px the bar is brand + actions only and there is nothing to
              collide. Removing the cap gives them MORE room at every width, never
              less. */}
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex h-14 items-center justify-between">
              <div className="flex items-center gap-8">
                <Link
                  to="/"
                  className="flex items-center gap-2 font-semibold tracking-tight text-ink"
                >
                  {/* 256x216 for a 28px slot, because the header renders at 2x and 3x
                      on the laptops this is used from and a 28px source would be soft
                      there. `object-contain` inside the square box fits the 1.185:1
                      mark rather than stretching it, and `size-7` matches the lettered
                      tile this replaced so the header's rhythm is unchanged.

                      No background tile: the PNG carries its own alpha — verified by
                      decoding it, every opaque pixel blue and none black — so it reads
                      correctly on both themes unaided.

                      `alt` is empty on purpose. The word "Support" sits immediately
                      beside it as real text, so a described image would be announced
                      twice to a screen reader. */}
                  <img
                    src="/adora-256.png"
                    alt=""
                    className="size-7 object-contain"
                    width="28"
                    height="28"
                  />
                  Support
                </Link>
                <div className="hidden sm:flex items-center gap-1">
                  {sections.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-sunken text-ink'
                            : 'text-ink-secondary hover:text-ink hover:bg-sunken'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Hidden from viewers because the create form would only fail at the
                    server for them. That is presentation — POST /api/tickets still
                    requires can_write, and so it should (docs/API.md §1.8). */}
                {canWrite && (
                  <Link to="/tickets/new" className="btn-primary">
                    <span aria-hidden="true">+</span> New ticket
                  </Link>
                )}

                {/* Left of the user chip on purpose: the chip is what people reach for
                    to confirm who they are signed in as, and it stays the rightmost
                    thing in the bar where every other workspace puts it. */}
                <ThemeToggle />

                <div className="flex items-center gap-2 pl-3 border-l border-line">
                  <div className="hidden sm:block text-right leading-tight">
                    <div className="text-sm font-medium text-ink">{user.display_name}</div>
                    <div className="text-xs text-ink-muted capitalize">{user.role}</div>
                  </div>
                  <div className="grid size-8 place-items-center rounded-full bg-sunken text-xs font-medium text-ink-secondary ring-1 ring-inset ring-line">
                    {initials(user.display_name)}
                  </div>

                  {/* A visible button rather than a menu behind the chip. This is a
                      shared support desk on a 12-hour rolling session
                      (src/middleware/session.js): the person who needs this is the one
                      standing up to let somebody else sit down, and a control they
                      have to go looking for is a control they will skip.

                      Hidden under the stub identity, and not for tidiness: with SSO
                      off, `createApp` does not mount the /auth router AT ALL
                      (src/server.js), so POST /auth/logout is a 404 — and even if it
                      answered, signing out of an identity that re-creates itself on
                      the next request is meaningless (docs/API.md §1.7). A button that
                      reports "you are still signed in" every time is worse than no
                      button. */}
                  {!user.auth_is_stub && (
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={handleSignOut}
                      disabled={signingOut}
                    >
                      {signingOut ? 'Signing out…' : 'Sign out'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {signOutError && (
          <div
            role="alert"
            className="border-b border-rose-200 bg-rose-50 px-4 py-1.5 text-center text-xs text-rose-900
                       dark:border-rose-500 dark:bg-rose-900 dark:text-rose-100"
          >
            You are still signed in. {signOutError}
          </div>
        )}

        {/* Local dev runs with a stub identity; make that impossible to mistake for
            real auth. `auth_is_stub` is false whenever SSO is the authentication in
            play — src/routes/auth.js sets it explicitly false at the callback — and the
            server refuses to boot with a stub identity outside development
            (`assertStubAuthAllowed`, src/server.js).

            The one hand-rolled dark treatment in this file. A warning band has
            to keep shouting on a dark page, where a subtle tint reads as chrome, so it
            takes a filled amber-900 with a brighter top border rather than the faint
            amber-50 wash that is enough against white. It stays distinct from the
            internal-note amber (SPEC.md §7.4, `bg-note`), which is darker and browner —
            these are the only two warm surfaces in the app and they must not be
            confusable with each other either. */}
        {user.auth_is_stub && (
          <div
            className="border-b px-4 py-1.5 text-center text-xs
                       bg-amber-50 border-amber-200 text-amber-900
                       dark:bg-amber-900 dark:border-amber-500 dark:text-amber-100"
          >
            Development mode, signed in as <strong>{user.email}</strong> (stub user).
            This is not Entra ID SSO: no password was checked and any browser on this
            machine is this user.
          </div>
        )}

        {/* Content, unlike the chrome above, IS measured — see WIDE_CONTENT_ROUTES. */}
        <main className={`mx-auto ${contentWidth} px-4 py-6 sm:px-6 lg:px-8`}>
          <Outlet />
        </main>
      </div>
    </ThemeProvider>
  );
}
